import type { TranslationKey } from "@privacy/translations";
import type { ComponentType, FC } from "react";
import { useTranslation } from "@privacy/translations";
import { Stack, Typography } from "@mui/material";
import type { IconWithBackgroundProps } from "../../shared/icons";
import { partnerByIdQueryOptions } from "../../api/partners";
import type { PartnerRole, RoleConnectedEntitiesFormType } from "../../api/partners";
import { DynamicGrid } from "../../dynamicGrid";
import { Drawer } from "../../shared/Drawer";
import type { Paths } from "../../shared/usePrivacyNavigate";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import {
  RegistrationProcessorIcon1,
  RegistrationProcessorIcon2,
  RegistrationControllerIcon1,
  RegistrationControllerIcon2,
  SystemIcon1,
  SystemIcon2,
} from "../../shared/icons";
import { PartnerDrawerHeader } from "./PartnerDrawerHeader";
import { NotAccessibleConnectedEntitiesInfo } from "./ReplaceAndArchiveDialog";
import { useQuery } from "@tanstack/react-query";

export const CONNECTED_ENTITIES_DRAWER_PATH_SUFFIX = "connected-entities";

type PartnerSubDrawerConnectedEntitiesProps = { role: PartnerRole };

export const PartnerSubDrawerConnectedEntities: FC<PartnerSubDrawerConnectedEntitiesProps> = ({ role }) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const partnerId = DynamicGrid.useSelectedRowId();
  const partnerByIdQuery = useQuery(partnerByIdQueryOptions(partnerId));

  return (
    <DynamicGrid.Drawer sx={{ pb: 0 }}>
      {(() => {
        if (!partnerByIdQuery.data) return <Drawer.Loading />;

        const partner = partnerByIdQuery.data;
        const roleData = partner.rolesAndRelationships.find(r => r.role === role);

        if (!roleData) {
          throw new Error(`No role data found for partner ${partnerId} and role ${role}`);
        }

        const { connectedEntities } = roleData;

        const { Icon1, Icon2, GridNoRowsOverlay, getPath } = CONNECTED_ENTITIES_FORM_TYPES_INFO[roleData.formType];
        const notAccessibleConnectedEntitiesExists = roleData.totalConnectedEntitiesCount > connectedEntities.length;

        return (
          <Stack sx={{ height: "100%" }}>
            <PartnerDrawerHeader status="hidden" partner={partner} onClose={() => navigate(paths.partnersGrid())} />
            <Drawer.SubDrawerHeader
              onBackClick={() => navigate("..", { relative: "path" })}
              title={t(CONNECTED_ENTITIES_FORM_TYPES_INFO[roleData.formType].subDrawerButtonLabelKey)}
              renderIcon={props => <Icon1 {...props} />}
              count={roleData.totalConnectedEntitiesCount}
              countAsterisk={notAccessibleConnectedEntitiesExists ? true : false}
            />
            <Drawer.Grid
              getRowId={row => row.responseId}
              onRowClick={row => navigate(getPath(paths, String(row.id)))}
              rows={connectedEntities}
              slots={{
                toolbar: Drawer.GridToolbar,
                noRowsOverlay: GridNoRowsOverlay,
              }}
              columns={[
                {
                  field: "name",
                  flex: 1,
                  headerName: t("PartnersGrid.NameHeader"),
                  valueGetter: (_, { responseName }) => responseName,
                  renderCell: ({ row }) => (
                    <Drawer.GridCell>
                      <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
                        <Icon2 size="sm" />
                        <Stack>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: "600", lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }}
                          >
                            {row.responseName}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Drawer.GridCell>
                  ),
                },
              ]}
            />
            {notAccessibleConnectedEntitiesExists && (
              <NotAccessibleConnectedEntitiesInfo
                formType={roleData.formType}
                totalConnectedEntitiesCount={roleData.totalConnectedEntitiesCount}
                accessibleConnectedEntitiesCount={connectedEntities.length}
              />
            )}
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

export const CONNECTED_ENTITIES_FORM_TYPES_INFO = {
  privacyRegistration: {
    roleCardLabelKey: "Partner.Role.AssociatedFormType.PrivacyRegistration.RoleCard",
    subDrawerButtonLabelKey: "Partner.Role.AssociatedFormType.PrivacyRegistration.SubDrawerButton",
    Icon1: RegistrationControllerIcon1,
    Icon2: RegistrationControllerIcon2,
    getPath: (path: Paths, id: string) => path.registrationsInline({ id, variant: "Controller" }),
    GridNoRowsOverlay: () => <Drawer.GridNoRowsOverlay labelKey="Partner.SubDrawer.ConnectedEntities.EmptyState" />,
  },
  privacyProcessorRegistration: {
    roleCardLabelKey: "Partner.Role.AssociatedFormType.PrivacyProcessorRegistration.RoleCard",
    subDrawerButtonLabelKey: "Partner.Role.AssociatedFormType.PrivacyProcessorRegistration.SubDrawerButton",
    Icon1: RegistrationProcessorIcon1,
    Icon2: RegistrationProcessorIcon2,
    getPath: (path: Paths, id: string) => path.registrationsInline({ id, variant: "Processor" }),
    GridNoRowsOverlay: () => <Drawer.GridNoRowsOverlay labelKey="Partner.SubDrawer.ConnectedEntities.EmptyState" />,
  },
  system: {
    roleCardLabelKey: "Partner.Role.AssociatedFormType.System.RoleCard",
    subDrawerButtonLabelKey: "Partner.Role.AssociatedFormType.System.SubDrawerButton",
    Icon1: SystemIcon1,
    Icon2: SystemIcon2,
    getPath: (path: Paths, id: string) => path.systemsInline(id),
    GridNoRowsOverlay: () => (
      <Drawer.GridNoRowsOverlay labelKey="Partner.SubDrawer.ConnectedEntities.EmptyState.System" />
    ),
  },
} satisfies Record<
  RoleConnectedEntitiesFormType,
  {
    roleCardLabelKey: TranslationKey;
    subDrawerButtonLabelKey: TranslationKey;
    Icon1: ComponentType<IconWithBackgroundProps>;
    Icon2: ComponentType<IconWithBackgroundProps>;
    GridNoRowsOverlay: ComponentType;
    getPath: (path: Paths, id: string) => string;
  }
>;
